import React from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';

/**
 * <FilterBar />
 *
 * Single-row filter bar replacing the 3-row hand-rolled patterns. Active
 * filters render as removable chips below the search row so users always
 * see what's applied.
 *
 * Props:
 *   search: { value, onChange, placeholder }
 *   filters: Array<{
 *     key, label, value, onChange, options: [{value, label}],
 *     defaultLabel?: string ("All ...")
 *   }>
 *   resultCount?: number
 *   onReset?: () => void
 */
export default function FilterBar({ search, filters = [], resultCount, onReset }) {
  const activeFilters = filters.filter((f) => f.value && f.value !== 'all' && f.value !== '');
  const hasActive = activeFilters.length > 0 || (search?.value && search.value.length > 0);

  return (
    <div className="gov-card flex flex-col gap-2 px-4 py-3" data-testid="gov-filter-bar">
      <div className="flex flex-wrap items-center gap-2">
        {search && (
          <div className="relative min-w-[200px] flex-1">
            <Search className="gov-text-tertiary absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
            <Input
              type="search"
              value={search.value || ''}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder || 'Search…'}
              className="h-9 border-[var(--gov-border)] bg-[var(--gov-bg-input)] pl-8 text-sm"
              data-testid="gov-filter-search"
            />
          </div>
        )}

        {filters.map((f) => (
          <Select key={f.key} value={f.value || 'all'} onValueChange={f.onChange}>
            <SelectTrigger
              className="h-9 w-auto min-w-[140px] border-[var(--gov-border)] bg-[var(--gov-bg-input)] text-sm"
              data-testid={`gov-filter-${f.key}`}
            >
              <SelectValue placeholder={f.defaultLabel || `All ${f.label}`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{f.defaultLabel || `All ${f.label}`}</SelectItem>
              {f.options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}

        {resultCount !== undefined && (
          <span className="gov-text-tertiary ml-auto text-xs" data-testid="gov-filter-result-count">
            {resultCount} {resultCount === 1 ? 'result' : 'results'}
          </span>
        )}
      </div>

      {/* Active filter chips */}
      {hasActive && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="gov-filter-chips">
          {search?.value && (
            <Chip
              label={`"${search.value}"`}
              onRemove={() => search.onChange('')}
              testId="gov-filter-chip-search"
            />
          )}
          {activeFilters.map((f) => {
            const opt = f.options.find((o) => o.value === f.value);
            return (
              <Chip
                key={f.key}
                label={`${f.label}: ${opt?.label || f.value}`}
                onRemove={() => f.onChange('all')}
                testId={`gov-filter-chip-${f.key}`}
              />
            );
          })}
          {onReset && (
            <button
              onClick={onReset}
              className="gov-text-tertiary hover:gov-text-primary text-xs underline-offset-2 hover:underline"
              data-testid="gov-filter-reset"
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Chip({ label, onRemove, testId }) {
  return (
    <Badge
      variant="outline"
      className="gov-tabular gap-1 border-[var(--gov-accent-border)] bg-[var(--gov-accent-bg)] py-0.5 pl-2 pr-1 text-[11px] font-normal text-[var(--gov-accent)]"
      data-testid={testId}
    >
      <span>{label}</span>
      <button
        onClick={onRemove}
        className="hover:bg-[var(--gov-accent)]/20 rounded-sm p-0.5"
        aria-label="Remove filter"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </Badge>
  );
}
